import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { Plus, Users } from 'lucide-react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { fetchBoard } from '../../store/boardSlice';
import { fetchTasks, updateTask, patchTaskLocal } from '../../store/taskSlice';
import { fetchWorkspace } from '../../store/workspaceSlice';
import { showToast } from '../../store/uiSlice';
import { Task, TaskStatus } from '../../types/common';
import { usePermissions } from '../../hooks/usePermissions';
import KanbanBoard from '../../components/Board/KanbanBoard';
import CreateTaskModal from '../../components/Tasks/CreateTaskModal';
import BoardAccessDialog from '../../components/Board/BoardAccessDialog';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import './BoardPage.scss';

const BoardPage: React.FC = () => {
  const { boardId } = useParams<{ boardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const taskId = searchParams.get('taskId');
  const dispatch = useAppDispatch();
  const { currentBoard, loading: boardLoading } = useAppSelector((state) => state.boards);
  const { tasks, loading: tasksLoading } = useAppSelector((state) => state.tasks);
  const { currentWorkspace } = useAppSelector((state) => state.workspaces);
  const { canCreateTask, isOwnerOrAdmin } = usePermissions(currentBoard?.workspaceId);

  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
  const [defaultTaskStatus, setDefaultTaskStatus] = useState<TaskStatus>(TaskStatus.TODO);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);

  useEffect(() => {
    if (boardId) {
      dispatch(fetchBoard(boardId));
      dispatch(fetchTasks(boardId));
    }
  }, [boardId, dispatch]);

  useEffect(() => {
    if (currentBoard?.workspaceId && !currentWorkspace) {
      dispatch(fetchWorkspace(currentBoard.workspaceId));
    }
  }, [currentBoard, currentWorkspace, dispatch]);

  const handleCreateTask = (status: TaskStatus) => {
    setDefaultTaskStatus(status);
    setCreateTaskModalOpen(true);
  };

  // Returns whether the update succeeded, so callers that need to react
  // differently to success vs. failure (e.g. TaskDetail's assignee picker,
  // which shows its own success toast) don't have to guess - failure is
  // already reported here via toast, so callers only need to handle success.
  const handleUpdateTask = async (taskId: string, updates: Partial<Task>): Promise<boolean> => {
    // Apply immediately (e.g. dragging a card to another column) so the UI
    // never waits on the network, then reconcile with the server response.
    const previousTask = tasks.find((task) => task.id === taskId);
    dispatch(patchTaskLocal({ taskId, updates }));

    try {
      await dispatch(updateTask({ taskId, data: updates })).unwrap();
      return true;
    } catch (error: any) {
      if (previousTask) {
        dispatch(patchTaskLocal({ taskId, updates: previousTask }));
      }
      // updateTask's thunk rejects with a plain string (via rejectWithValue)
      // carrying the backend's actual validation message, not an Error
      // object - fall back to .message only for the rare non-string case.
      dispatch(
        showToast({
          message: (typeof error === 'string' ? error : error?.message) || 'Failed to update task',
          type: 'error',
        })
      );
      return false;
    }
  };

  const handleClearTaskId = () => {
    if (searchParams.has('taskId')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('taskId');
      setSearchParams(newParams, { replace: true });
    }
  };

  if (boardLoading || tasksLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentBoard) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Board not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="board-page">
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Breadcrumbs */}
        <Breadcrumbs
          items={[
            { label: 'Workspaces', path: '/workspaces' },
            ...(currentWorkspace
              ? [{ label: currentWorkspace.name, path: `/workspace/${currentWorkspace.id}` }]
              : []),
            { label: currentBoard.name },
          ]}
        />

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mt: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
            {currentBoard.key && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {currentBoard.key}
              </Typography>
            )}
            <Typography variant="h4" component="h1" gutterBottom noWrap>
              {currentBoard.name}
            </Typography>
            {currentBoard.description && (
              <Typography variant="body2" color="text.secondary">
                {currentBoard.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
            {isOwnerOrAdmin && (
              <Button
                variant="outlined"
                startIcon={<Users size={18} />}
                onClick={() => setAccessDialogOpen(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Manage Access
              </Button>
            )}
            {canCreateTask && (
              <Button
                variant="contained"
                startIcon={<Plus size={18} />}
                onClick={() => handleCreateTask(TaskStatus.TODO)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Add Task
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Kanban Board */}
      <Box sx={{ p: 3 }}>
        <KanbanBoard
          tasks={tasks}
          onUpdateTask={handleUpdateTask}
          onCreateTask={handleCreateTask}
          canCreateTask={canCreateTask}
          initialTaskId={taskId || undefined}
          onClearInitialTask={handleClearTaskId}
        />
      </Box>

      {/* Create Task Modal */}
      {boardId && (
        <CreateTaskModal
          open={createTaskModalOpen}
          onClose={() => setCreateTaskModalOpen(false)}
          boardId={boardId}
          defaultStatus={defaultTaskStatus}
        />
      )}

      {/* Project Access (members/teams) */}
      {boardId && (
        <BoardAccessDialog
          open={accessDialogOpen}
          onClose={() => setAccessDialogOpen(false)}
          boardId={boardId}
          workspaceId={currentBoard.workspaceId}
          canManage={isOwnerOrAdmin}
        />
      )}
    </Box>
  );
};

export default BoardPage;
